
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useInfluencerData } from "@/hooks/useInfluencerData";
import { ProfileHeader } from "@/components/influencer/ProfileHeader";
import { SocialProfiles } from "@/components/influencer/SocialProfiles";
import { PricingInformation } from "@/components/influencer/PricingInformation";
import { motion } from "framer-motion";

const InfluencerDetail = () => {
  const { t, language } = useLanguage();
  const { influencerId } = useParams<{ influencerId: string }>();
  const { influencer, isLoading } = useInfluencerData(influencerId);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center min-h-[50vh] overflow-x-hidden">
        <motion.div 
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <div className="absolute inset-0 w-16 h-16 border-4 border-primary/30 rounded-full"></div>
        </motion.div>
      </div>
    );
  }

  if (!influencer) {
    return (
      <div className="container mx-auto px-4 py-16 text-center overflow-x-hidden">
        <div className="max-w-md mx-auto p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10">
          <h1 className="text-2xl font-bold mb-4 text-primary">Influencer Not Found</h1>
          <p className="mb-8 text-muted-foreground">The influencer you're looking for could not be found.</p>
          <Button asChild variant="outline" className="border-primary/10 hover:bg-primary/5">
            <Link to="/influencers">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Influencers
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={`container mx-auto px-4 py-12 ${language === 'ar' ? 'text-right' : 'text-left'} overflow-x-hidden max-w-full`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" asChild className="border-primary/10 hover:bg-primary/5">
          <Link to="/influencer-booking">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("backToInfluencers")}
          </Link>
        </Button>
      </div>

      {/* Influencer Profile Header */}
      <ProfileHeader influencer={influencer} />

      {/* Content */}
      <div className="mt-8 space-y-8">
        <SocialProfiles influencer={influencer} />
        <PricingInformation influencer={influencer} />
      </div>
    </motion.div>
  );
};

export default InfluencerDetail;
